import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState, useCallback } from 'react';
import { Card, Col, InputGroup, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical } from '@fortawesome/free-solid-svg-icons';

import { FormProvider, RHFSelect } from 'components/common/form';
import LoadingButton from 'components/common/LoadingButton';
import { Can } from 'routes/permissions/Can';
import { userActions } from 'routes/permissions/permissionKeys';
import StrictModeDroppable from 'components/projects/kanban/StrictModeDroppable';
import {
  useWebsiteSettings,
  useDonationTypes,
  usePackages,
  useUpsertWebsiteSettings,
  useUpdatePackageOrder
} from 'hooks/useDonationSettings';

const websiteSettingsSchema = Yup.object().shape({
  defaultPackageId: Yup.string().required('Default package is required'),
  defaultDonationTypeId: Yup.string().required(
    'Default donation type is required'
  ),
  preselectedAmount: Yup.string().required('Preselected amount is required')
});

const WebsiteSettings = ({ sectionRef, sideNavLink, subject }) => {
  const { websiteSettings, isLoading: settingsLoading } = useWebsiteSettings();
  const { donationTypes, isLoading: donationTypesLoading } = useDonationTypes();
  const { packages, isLoading: packagesLoading } = usePackages({ is_hidden: false });
  const { upsertWebsiteSettings, isUpdating: isSaveWebsiteSettingsLoading } = useUpsertWebsiteSettings();
  const { updatePackageOrder, isUpdating: isOrderUpdating } = useUpdatePackageOrder();

  const [orderedPackages, setOrderedPackages] = useState([]);

  useEffect(() => {
    if (packages?.length) {
      setOrderedPackages(packages);
    }
  }, [packages]);

  const methods = useForm({
    mode: 'onBlur',
    defaultValues: {
      defaultPackageId: '',
      defaultDonationTypeId: '',
      preselectedAmount: ''
    },
    resolver: yupResolver(websiteSettingsSchema)
  });
  const { handleSubmit: RHFHandleSubmit, reset: resetForm } = methods;

  const onSave = async (data) => {
    try {
      await upsertWebsiteSettings({
        default_package_id: data.defaultPackageId,
        default_donation_type_id: data.defaultDonationTypeId,
        preselected_amount: data.preselectedAmount
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (websiteSettings) {
      resetForm({
        defaultPackageId: websiteSettings.default_package_id || '',
        defaultDonationTypeId: websiteSettings.default_donation_type_id || '',
        preselectedAmount: websiteSettings.preselected_amount || ''
      });
    }
  }, [websiteSettings, resetForm]);

  const handleDragEnd = useCallback(
    async (result) => {
      if (!result.destination) return;
      if (result.destination.index === result.source.index) return;

      const reordered = Array.from(orderedPackages);
      const [moved] = reordered.splice(result.source.index, 1);
      reordered.splice(result.destination.index, 0, moved);

      setOrderedPackages(reordered);

      const updates = reordered.map((pkg, index) => ({
        id: pkg.id,
        display_order: index
      }));

      try {
        await updatePackageOrder(updates);
      } catch (error) {
        console.error(error);
        setOrderedPackages(packages);
      }
    },
    [orderedPackages, updatePackageOrder, packages]
  );

  const donationTypeOptions =
    donationTypes?.map(({ id, donation_type_name }) => ({
      label: donation_type_name,
      value: id
    })) || [];

  const packageOptions =
    packages?.map(({ id, package_title, simple_id }) => ({
      label: `${simple_id} | ${package_title}`,
      value: id
    })) || [];

  const preselectedAmountOptions = [
    { label: 'Lowest', value: 'lowest' },
    { label: 'Average', value: 'average' },
    { label: 'Highest', value: 'highest' }
  ];

  return (
    <FormProvider methods={methods} onSubmit={RHFHandleSubmit(onSave)}>
      <Card
        ref={sectionRef}
        id={sideNavLink}
        className="border shadow-none mb-3"
      >
        <Card.Header>
          <Row>
            <Col>
              <Card.Title>Website Settings</Card.Title>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <InputGroup className="mb-3">
            <div className="flex-grow-1">
              <RHFSelect
                name="defaultPackageId"
                label="Default Package"
                options={packageOptions}
                placeholder="Select Default Package"
                required
                isLoading={packagesLoading}
              />
            </div>
          </InputGroup>
          <InputGroup className="mb-3">
            <div className="flex-grow-1">
              <RHFSelect
                name="defaultDonationTypeId"
                label="Default Donation Type"
                options={donationTypeOptions}
                placeholder="Select Default Donation Type"
                required
                isLoading={donationTypesLoading}
              />
            </div>
          </InputGroup>
          <InputGroup className="mb-3">
            <div className="flex-grow-1">
              <RHFSelect
                name="preselectedAmount"
                label="Preselected Amount"
                options={preselectedAmountOptions}
                placeholder="Select Preselected Amount"
                required
              />
            </div>
          </InputGroup>

          <hr className="my-4" />

          <h6 className="mb-3 fw-bold">Package Display Order</h6>
          <p className="text-muted fs-10 mb-3">
            Drag and drop to reorder how packages appear on the website.
          </p>

          {packagesLoading ? (
            <p className="text-muted">Loading packages...</p>
          ) : orderedPackages.length === 0 ? (
            <p className="text-muted">No packages found.</p>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <StrictModeDroppable droppableId="package-order">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {orderedPackages.map((pkg, index) => (
                      <Draggable
                        key={pkg.id}
                        draggableId={pkg.id}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={`d-flex align-items-center p-2 mb-2 rounded border ${
                              snapshot.isDragging
                                ? 'shadow-sm bg-light'
                                : 'bg-white'
                            }`}
                            style={{
                              ...provided.draggableProps.style,
                              cursor: 'grab'
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faGripVertical}
                              className="text-muted me-3"
                            />
                            <div>
                              <span className="fw-semibold">
                                {pkg.package_title}
                              </span>
                              <span className="text-muted ms-2 fs-10">
                                {pkg.simple_id}
                              </span>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </StrictModeDroppable>
            </DragDropContext>
          )}
        </Card.Body>
        <Card.Footer className="d-flex justify-content-end">
          <Can I={userActions.update} a={subject}>
            <LoadingButton
              loading={isSaveWebsiteSettingsLoading}
              variant="light"
              type="submit"
              className="border bg-transparent shadow-none align-self-end"
              style={{ fontWeight: 600 }}
            >
              Save
            </LoadingButton>
          </Can>
        </Card.Footer>
      </Card>
    </FormProvider>
  );
};

WebsiteSettings.propTypes = {
  sectionRef: PropTypes.object,
  sideNavLink: PropTypes.string,
  subject: PropTypes.string
};

export default WebsiteSettings;
